import {
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  SideBarDef
} from "ag-grid-enterprise";
import { numberFilterParams } from "components/cell-editor/numberCellEditor";
import {
  COMMENTS_EXPORT_COLUMN_ID,
  formatCommentsForExport
} from "utils/comments-export";
import { WarrantyValueFormatter } from "components/cell-editor/warranty-cell-editor";
import {
  CellValueRendererWithEditIcon,
  dueDateCellRenderer,
  featureCommentActionCellRenderer,
  LBSCellRenderer,
  materialDueDateHeaderComponent,
  riskCellRenderer,
  SubmittalLinksEntityCellRenderer
} from "components/cell-renders";
import {
  ArrayStringComparator,
  ArrayStringGetQuickFilterText,
  materialEarlyReleaseCellRenderer,
  twoRowCellRenderer,
  twoRowGetQuickFilterText
} from "entity-app/shared-components/log-render-components/cell-renders";
import {
  coldefHiddenField,
  coldefLinkedActivities,
  coldefSingleField,
  coldefUserV2,
  dateTimeAuditColumnOverrides
} from "entity-app/shared-components/log-render-components/col-defs";
import {
  headerValueGetter,
  shouldSpanRow,
  dateCellValueGetter,
  headerTooltipValueGetter,
  getColumnsForDateBlock,
  getCustomFieldValue,
  getFinalResponseText,
  MaterialCustomColumn
} from "pages/reports-menu/project-reports/feature-custom-reports/utils/utils";
import { useMemo, useRef } from "react";
import { EnumOptionsMap } from "hooks/enum-values";
import { RiskLevelType } from "components/date-block/models";
import CustomTreeFilter from "components/grid-filters/custom-tree-filter";
import { DateFilter as DateFilterUtils } from "utils/dateutils";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import { FeatureTypes } from "entity-app/constants";
import {
  comparatorWithEmptyAtBottom,
  riskLevelComparator
} from "utils/grid-utils";
import { ContextDatasetConfigType } from "../models";
import {
  LINKED_SUBMITTALS_KEY,
  LINKED_ACTIVITIES_KEY,
  LINKED_SUBMITTAL_DESCRIPTION_KEY,
  LINKED_SUBMITTAL_FINAL_RESPONSE_KEY,
  MATERIAL_DESCRIPTION_KEY
} from "../custom-reports-constants";

const buildCustomFieldColDef = (
  customColumn: MaterialCustomColumn,
  getEnumOptionsMap: () => EnumOptionsMap | undefined
): ColDef => {
  const isDate = customColumn.field_type === "date";
  const isNumber = customColumn.field_type === "number";
  return coldefSingleField({
    colId: customColumn.field,
    headerValueGetter: (params: any) => {
      const caption = headerValueGetter(params, customColumn.field);
      return caption && caption !== customColumn.field
        ? caption
        : customColumn.headerName || customColumn.field;
    },
    valueGetter: ({ data }: any) =>
      getCustomFieldValue(data, customColumn, getEnumOptionsMap()),
    spanRows: (params: any) => {
      return shouldSpanRow({
        path: customColumn.field,
        nodeAData: params.nodeA.data,
        nodeBData: params.nodeB.data
      });
    },
    ...(isDate
      ? {
          filter: CustomDateSetFilter,
          cellDataType: "dateString",
          comparator: DateFilterUtils.comparator
        }
      : {}),
    ...(isNumber
      ? { filter: "agNumberColumnFilter", filterParams: numberFilterParams }
      : {})
  }) as ColDef;
};

function useReportColumnDef({
  groupByField,
  linkedEntityDisplayType,
  isLBSFeatureEnabled,
  isMaterialCsiClassificationEnabled,
  projectId,
  reportItems,
  materialCustomColumns,
  enumOptionsMap
}: {
  groupByField: ContextDatasetConfigType["groupByField"];
  linkedEntityDisplayType: ContextDatasetConfigType["linkedEntity"];
  isLBSFeatureEnabled: Boolean;
  isMaterialCsiClassificationEnabled: boolean;
  projectId: string;
  reportItems: any[];
  materialCustomColumns?: MaterialCustomColumn[];
  enumOptionsMap?: EnumOptionsMap;
}) {
  // Rebuilding column defs makes AG Grid re-apply each def's `hide`/`width`,
  // undoing the user's column state. Enum labels are read through a ref (the
  // caller refreshes cells when they load) and custom columns are keyed by
  // content, since column-headers returns a new array on every refetch.
  const enumOptionsMapRef = useRef(enumOptionsMap);
  enumOptionsMapRef.current = enumOptionsMap;
  const materialCustomColumnsKey = JSON.stringify(materialCustomColumns ?? []);
  const customFieldColumnDefs = useMemo(
    () =>
      (JSON.parse(materialCustomColumnsKey) as MaterialCustomColumn[])
        .filter((customColumn) => !!customColumn?.field)
        .map((customColumn) =>
          buildCustomFieldColDef(customColumn, () => enumOptionsMapRef.current)
        ),
    [materialCustomColumnsKey]
  );

  const submittalDisplayType = useMemo(() => {
    if (!linkedEntityDisplayType) return undefined;
    return linkedEntityDisplayType.find(
      (entity: any) => entity.feature_type_id === 1
    )?.display_type;
  }, [linkedEntityDisplayType]);

  const activityDisplayType = useMemo(() => {
    if (!linkedEntityDisplayType) return undefined;
    return linkedEntityDisplayType.find(
      (entity: any) => entity.feature_type_id === 9
    )?.display_type;
  }, [linkedEntityDisplayType]);

  const dateBlockColumns = useMemo(() => {
    if (!Array.isArray(reportItems) || reportItems.length === 0) {
      return {
        dateBlockGroupCols: [] as ColDef[],
        dateBlockColumnDefs: [] as ColDef[],
        dateBlockColumnIds: {
          milestones: [] as string[],
          offsets: [] as string[]
        }
      };
    }
    return getColumnsForDateBlock(reportItems);
  }, [reportItems]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      editable: false,
      filter: true,
      resizable: true,
      cellRenderer: CellValueRendererWithEditIcon,
      headerTooltipValueGetter,
      comparator: comparatorWithEmptyAtBottom,
      // Disable AG Grid cell data type inference; we handle formatting ourselves
      cellDataType: false,
      // With enableCellSpan, alpine cells vertically center short renderers; keep values top-aligned.
      cellStyle: { alignItems: "flex-start" }
    };
  }, []);

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      minWidth: 300,
      headerValueGetter: (params: any) => {
        if (groupByField === "none") return "No Grouping";
        const groupColHeader = headerValueGetter(params, groupByField);
        return `Group by ${groupColHeader}`;
      },
      context: { groupByField: groupByField || "none" },
      filter: "agGroupColumnFilter",
      sortable: true,
      suppressMovable: true,
      lockPosition: true,
      cellRendererParams: {
        suppressCount: true
      },
      spanRows: true,
      autoHeight: true,
      cellClass: `dataset_group_column dataset_groupby_class_${groupByField}`
    };
  }, [groupByField]);

  const sideBarConfig: SideBarDef = useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressSideButtons: true,
            suppressColumnSelectAll: true
          }
        }
      ],
      position: "right"
    };
  }, []);

  const columnDefs: (ColDef<any> | ColGroupDef<any>)[] = useMemo(() => {
    return [
      {
        field: "sequence_id",
        colId: "sequence_id",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "sequence_id");
        },
        initialWidth: 120,
        spanRows: true,
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams
      },
      coldefSingleField({
        colId: "title",
        field: "title",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "title");
        },
        cellRenderer: twoRowCellRenderer,
        cellRendererParams: {
          featureKey: "materials",
          projectId
        },
        getQuickFilterText: twoRowGetQuickFilterText,
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "title",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        filter: true
      }),
      coldefSingleField({
        colId: MATERIAL_DESCRIPTION_KEY,
        field: MATERIAL_DESCRIPTION_KEY,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, MATERIAL_DESCRIPTION_KEY);
        },
        cellClass: "excelWrapText",
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: MATERIAL_DESCRIPTION_KEY,
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        colId: "milestone_state",
        field: "milestone_state",
        filter: "agSetColumnFilter",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "milestone_state");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "milestone_state",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "wf_override_reason",
        colId: "wf_override_reason",
        filter: "agSetColumnFilter",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "wf_override_reason");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "wf_override_reason",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        cellRenderer: materialEarlyReleaseCellRenderer,
        valueGetter: ({ data }: any) => {
          if (data?.wf_override_reason) return "Yes";
          return "";
        }
      }),
      {
        field: "planned_next_milestone_date",
        colId: "planned_next_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "planned_next_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "planned_next_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },
      {
        field: "next_planned_deadline",
        colId: "next_planned_deadline",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "next_planned_deadline");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "next_planned_deadline",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },
      coldefSingleField({
        field: "next_action",
        colId: "next_action",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "next_action");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "next_action",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      {
        field: "final_deadline_date",
        colId: "final_deadline_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "final_deadline_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "final_deadline_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },
      {
        field: "projected_last_milestone_date",
        colId: "projected_last_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "projected_last_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "projected_last_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },

      {
        field: "due_date",
        colId: "due_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "due_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "due_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        cellRenderer: (params: ICellRendererParams) => {
          return dueDateCellRenderer(params, false);
        },
        headerComponentParams: materialDueDateHeaderComponent,
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },
      coldefSingleField({
        field: "lead_time",
        colId: "lead_time",
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "lead_time");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "lead_time",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        colId: "risk",
        field: "risk",
        comparator: riskLevelComparator,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "risk");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "risk",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        cellRenderer: (params: ICellRendererParams) => {
          return riskCellRenderer(params, false);
        },
        cellClassRules: {
          highRisk: ({ value }) => {
            return value === RiskLevelType.High;
          },
          mediumRisk: ({ value }) => {
            return value === RiskLevelType.Medium;
          },
          lowRisk: ({ value }) => {
            return value === RiskLevelType.Low;
          }
        }
      }),
      coldefSingleField({
        field: "planned_float",
        colId: "planned_float",
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "planned_float");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "planned_float",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "actual_float",
        colId: "actual_float",
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "actual_float");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "actual_float",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),

      coldefSingleField({
        colId: "spec_sections",
        field: "spec_sections",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "spec_sections");
        },
        rowGroup: groupByField === "spec_sections",
        initialHide: groupByField === "spec_sections",
        suppressColumnsToolPanel: groupByField === "spec_sections",
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "spec_sections",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      ...(isMaterialCsiClassificationEnabled
        ? [
            coldefSingleField({
              colId: "divisions",
              field: "divisions",
              headerValueGetter: (params: any) => {
                return headerValueGetter(params, "divisions");
              },
              filter: "agSetColumnFilter",
              valueGetter: ({ data }: any) => {
                if (!Array.isArray(data?.divisions) || !data.divisions.length)
                  return "";
                return data.divisions[0];
              },
              spanRows: (params: any) => {
                return shouldSpanRow({
                  path: "divisions",
                  nodeAData: params.nodeA.data,
                  nodeBData: params.nodeB.data
                });
              }
            })
          ]
        : []),
      coldefSingleField({
        field: "purchase_order",
        colId: "purchase_order",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "purchase_order");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "purchase_order",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "ready_for_release",
        colId: "ready_for_release",
        filter: "agSetColumnFilter",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "ready_for_release");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "ready_for_release",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: (params: any) => {
          // Group rows have no material data; don't show Ready / Not Ready there.
          if (params.node?.group || !params.data) return "";
          return params.data.ready_for_release ? "Ready" : "Not Ready";
        }
      }),
      coldefSingleField({
        field: "tag_name",
        colId: "tag_name",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "tag_name");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "tag_name",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "date_block_template_name",
        colId: "date_block_template_name",
        filter: "agSetColumnFilter",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "date_block_template_name");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "date_block_template_name",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),

      ...(isLBSFeatureEnabled
        ? [
            coldefSingleField({
              field: "project_locations",
              colId: "project_locations",
              headerValueGetter: (params: any) => {
                return headerValueGetter(params, "project_locations");
              },
              spanRows: (params: any) => {
                return shouldSpanRow({
                  path: "project_locations",
                  nodeAData: params.nodeA.data,
                  nodeBData: params.nodeB.data
                });
              },
              valueGetter: ({ data }) => {
                if (
                  !data ||
                  !data.project_locations ||
                  !data.project_locations.length
                )
                  return [];
                return data.project_locations.map((loc: any) => ({
                  id: loc.id || loc.path,
                  name: loc.path,
                  path: loc.path
                }));
              },
              filter: CustomTreeFilter,
              cellRenderer: LBSCellRenderer,
              getQuickFilterText: ArrayStringGetQuickFilterText,
              comparator: ArrayStringComparator
            })
          ]
        : []),

      coldefSingleField({
        colId: "quantity",
        field: "quantity",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "quantity");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "quantity",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: (param: any) =>
          `${param?.data?.quantity ?? ""} ${
            param?.data?.material_quantity_unit ?? ""
          }`.trim()
      }),

      coldefUserV2({
        def: {
          colId: "responsible_contractor_name",
          field: "responsible_contractor_name",
          headerValueGetter: (params: any) => {
            return headerValueGetter(params, "responsible_contractor_name");
          },
          spanRows: (params: any) => {
            return shouldSpanRow({
              path: "responsible_contractor_name",
              nodeAData: params.nodeA.data,
              nodeBData: params.nodeB.data
            });
          },
          rowGroup: groupByField === "responsible_contractor_name",
          initialHide: groupByField === "responsible_contractor_name",
          suppressColumnsToolPanel:
            groupByField === "responsible_contractor_name",
          cellClass: "pt-3"
        },
        user: {
          id: "responsible_contractor_id",
          firstName: "responsible_contractor_name",
          lastName: "",
          dropdownType: "RC_dropdown"
        }
      }),
      coldefUserV2({
        def: {
          colId: "assignee_user",
          field: "assignee_user",
          headerValueGetter: (params: any) => {
            return headerValueGetter(params, "assignee_user");
          },
          spanRows: (params: any) => {
            return shouldSpanRow({
              path: "assignee_user.id",
              nodeAData: params.nodeA.data,
              nodeBData: params.nodeB.data
            });
          },
          rowGroup: groupByField === "assignee_user",
          initialHide: groupByField === "assignee_user",
          suppressColumnsToolPanel: groupByField === "assignee_user",
          cellClass: "pt-3"
        },
        user: {
          id: "assignee_user.id",
          firstName: "assignee_user.name",
          lastName: "",
          statusId: "assignee_user.status_id",
          dropdownType: "RC_ASSIGNEE_dropdown",
          dependentId: "responsible_contractor_id",
          dependentNotFoundContent: "First select a Responsible Contractor",
          companyObjField: "assignee_user.company"
        }
      }),
      coldefUserV2({
        def: {
          colId: "gc_representative",
          field: "gc_representative",
          headerValueGetter: (params: any) => {
            return headerValueGetter(params, "gc_representative");
          },
          spanRows: (params: any) => {
            return shouldSpanRow({
              path: "gc_representative.id",
              nodeAData: params.nodeA.data,
              nodeBData: params.nodeB.data
            });
          },
          rowGroup: groupByField === "gc_representative",
          initialHide: groupByField === "gc_representative",
          suppressColumnsToolPanel: groupByField === "gc_representative",
          cellClass: "pt-3"
        },
        user: {
          id: "gc_representative.id",
          firstName: "gc_representative.name",
          lastName: "",
          statusId: "gc_representative.status_id",
          companyObjField: "gc_representative.company"
        }
      }),
      coldefSingleField({
        colId: "shipping_location",
        field: "shipping_location",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "shipping_location");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "shipping_location",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "manufacturer",
        colId: "manufacturer",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "manufacturer");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "manufacturer",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "material_size",
        colId: "material_size",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "material_size");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "material_size",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "part_number",
        colId: "part_number",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "part_number");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "part_number",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "serial_number",
        colId: "serial_number",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "serial_number");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "serial_number",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      coldefSingleField({
        field: "warranty",
        colId: "warranty",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "warranty");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "warranty",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueFormatter: WarrantyValueFormatter,
        filter: "agNumberColumnFilter",
        filterParams: numberFilterParams
      }),

      {
        field: "last_manual_updated_at",
        colId: "last_manual_updated_at",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "last_manual_updated_at");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "last_manual_updated_at",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        ...dateTimeAuditColumnOverrides("last_manual_updated_at")
      },
      {
        field: "last_system_updated_at",
        colId: "last_system_updated_at",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "last_system_updated_at");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "last_system_updated_at",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        ...dateTimeAuditColumnOverrides("last_system_updated_at")
      },

      {
        colId: "planned_milestone_1",
        field: "planned_milestone_1",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "planned_milestone_1");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "planned_milestone_1",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "actual_milestone_1",
        field: "actual_milestone_1",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "actual_milestone_1");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "actual_milestone_1",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "planned_last_milestone_date",
        field: "planned_last_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "planned_last_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "planned_last_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "actual_last_milestone_date",
        field: "actual_last_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "actual_last_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "actual_last_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "projected_next_milestone_date",
        field: "projected_next_milestone_date",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "projected_next_milestone_date");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "projected_next_milestone_date",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },
      {
        colId: "projected_milestone_1",
        field: "projected_milestone_1",
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, "projected_milestone_1");
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "projected_milestone_1",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        valueGetter: dateCellValueGetter,
        filter: CustomDateSetFilter,
        cellDataType: "dateString",
        comparator: DateFilterUtils.comparator
      },

      coldefSingleField({
        colId: LINKED_SUBMITTALS_KEY,
        field: LINKED_SUBMITTALS_KEY,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, LINKED_SUBMITTALS_KEY);
        },
        cellClass: "excelWrapText",
        minWidth: 400,
        initialHide: submittalDisplayType === "none",
        suppressColumnsToolPanel: submittalDisplayType === "none",
        resizable: submittalDisplayType !== "condensed",
        cellRenderer:
          submittalDisplayType === "condensed"
            ? SubmittalLinksEntityCellRenderer
            : undefined,
        marryChildren: submittalDisplayType === "inline",
        children:
          submittalDisplayType === "inline"
            ? [
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__sequence_id`,
                  field: `${LINKED_SUBMITTALS_KEY}__sequence_id`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__sequence_id`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: true,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__title`,
                  field: `${LINKED_SUBMITTALS_KEY}__title`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__title`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: true,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: LINKED_SUBMITTAL_DESCRIPTION_KEY,
                  field: LINKED_SUBMITTAL_DESCRIPTION_KEY,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      LINKED_SUBMITTAL_DESCRIPTION_KEY,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  cellClass: "excelWrapText",
                  filter: true,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__status`,
                  field: `${LINKED_SUBMITTALS_KEY}__status`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__status`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: "agSetColumnFilter",
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__planned_float`,
                  field: `${LINKED_SUBMITTALS_KEY}__planned_float`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__planned_float`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: "agNumberColumnFilter",
                  filterParams: numberFilterParams,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__actual_float`,
                  field: `${LINKED_SUBMITTALS_KEY}__actual_float`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__actual_float`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: "agNumberColumnFilter",
                  filterParams: numberFilterParams,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__risk`,
                  field: `${LINKED_SUBMITTALS_KEY}__risk`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__risk`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  filter: "agSetColumnFilter",
                  cellRenderer: (params: ICellRendererParams) => {
                    return riskCellRenderer(params, false);
                  },
                  cellClassRules: {
                    highRisk: ({ value }) => {
                      return value === RiskLevelType.High;
                    },
                    mediumRisk: ({ value }) => {
                      return value === RiskLevelType.Medium;
                    },
                    lowRisk: ({ value }) => {
                      return value === RiskLevelType.Low;
                    }
                  },
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__planned_milestone_2`,
                  field: `${LINKED_SUBMITTALS_KEY}__planned_milestone_2`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__planned_milestone_2`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  valueGetter: dateCellValueGetter,
                  filter: CustomDateSetFilter,
                  cellDataType: "dateString",
                  comparator: DateFilterUtils.comparator,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__actual_milestone_2`,
                  field: `${LINKED_SUBMITTALS_KEY}__actual_milestone_2`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__actual_milestone_2`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  valueGetter: dateCellValueGetter,
                  filter: CustomDateSetFilter,
                  cellDataType: "dateString",
                  comparator: DateFilterUtils.comparator,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__planned_milestone_4`,
                  field: `${LINKED_SUBMITTALS_KEY}__planned_milestone_4`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__planned_milestone_4`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  valueGetter: dateCellValueGetter,
                  filter: CustomDateSetFilter,
                  cellDataType: "dateString",
                  comparator: DateFilterUtils.comparator,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: `${LINKED_SUBMITTALS_KEY}__actual_milestone_4`,
                  field: `${LINKED_SUBMITTALS_KEY}__actual_milestone_4`,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      `${LINKED_SUBMITTALS_KEY}__actual_milestone_4`,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  valueGetter: dateCellValueGetter,
                  filter: CustomDateSetFilter,
                  cellDataType: "dateString",
                  comparator: DateFilterUtils.comparator,
                  sortable: false
                },
                {
                  columnGroupShow: "open",
                  colId: LINKED_SUBMITTAL_FINAL_RESPONSE_KEY,
                  field: LINKED_SUBMITTAL_FINAL_RESPONSE_KEY,
                  headerValueGetter: (params: any) => {
                    return headerValueGetter(
                      params,
                      LINKED_SUBMITTAL_FINAL_RESPONSE_KEY,
                      LINKED_SUBMITTALS_KEY
                    );
                  },
                  valueGetter: ({ data }: any) =>
                    getFinalResponseText(
                      data?.[LINKED_SUBMITTAL_FINAL_RESPONSE_KEY]
                    ),
                  filter: "agSetColumnFilter",
                  sortable: false
                }
              ]
            : undefined,
        filterValueGetter: ({ data }: any) => {
          const submittals = data?.linked_items || [];
          const hasSubmittal = submittals?.some(
            (x: any) => x?.feature_type === FeatureTypes.SUBMITTALS
          );
          return hasSubmittal ? "Submittal Linked" : "No Submittals Linked";
        }
      }),
      coldefLinkedActivities({
        colId: LINKED_ACTIVITIES_KEY,
        field: LINKED_ACTIVITIES_KEY,
        headerValueGetter: (params: any) => {
          return headerValueGetter(params, LINKED_ACTIVITIES_KEY);
        },
        cellClass: "excelWrapText",
        initialHide: activityDisplayType === "none",
        suppressColumnsToolPanel: activityDisplayType === "none",
        resizable: false,
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: LINKED_ACTIVITIES_KEY,
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      }),
      ...customFieldColumnDefs,
      ...dateBlockColumns.dateBlockGroupCols,
      ...dateBlockColumns.dateBlockColumnDefs,
      {
        // Matches the "comments" field_key in columns_config so its saved
        // visibility applies and tool-panel toggles persist.
        colId: "comments",
        headerName: "Comments",
        headerTooltip: "Comments",
        initialWidth: 110,
        editable: false,
        suppressColumnsToolPanel: false,
        suppressMovable: true,
        lockPosition: "right",
        // Gives the cell a value so it re-renders when the comment count changes.
        valueGetter: ({ data }: any) => data?.no_of_comments ?? 0,
        getQuickFilterText: () => "",
        cellRenderer: (params: ICellRendererParams) =>
          params.data ? featureCommentActionCellRenderer(params) : null,
        cellClass: "custom-report-action-cell",
        // Override defaultColDef flex-start so the icon stays centered in
        // tall cells (inline/spanned rows).
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        },
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "id",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        },
        filter: false,
        sortable: false,
        menuTabs: [],
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true
      },
      // Export-only: hidden in the grid, forced into Excel exports via
      // includedColumns and into the print view via forceVisibleColumnIds.
      coldefHiddenField({
        colId: COMMENTS_EXPORT_COLUMN_ID,
        field: "comments",
        headerName: "Comments",
        cellClass: ["excelWrapText", "!whitespace-pre-line"],
        wrapText: true,
        autoHeight: true,
        width: 360,
        suppressAutoSize: true,
        valueGetter: ({ data }: any) => formatCommentsForExport(data?.comments),
        spanRows: (params: any) => {
          return shouldSpanRow({
            path: "id",
            nodeAData: params.nodeA.data,
            nodeBData: params.nodeB.data
          });
        }
      })
    ];
  }, [
    projectId,
    customFieldColumnDefs,
    groupByField,
    isLBSFeatureEnabled,
    isMaterialCsiClassificationEnabled,
    submittalDisplayType,
    activityDisplayType,
    dateBlockColumns.dateBlockGroupCols,
    dateBlockColumns.dateBlockColumnDefs
  ]);

  return {
    columnDefs,
    defaultColDef,
    autoGroupColumnDef,
    sideBarConfig,
    dateBlockColumns
  };
}

export default useReportColumnDef;
